import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Dialog as MuiDialog,
  IconButton,
} from '@mui/material'
import React, { useMemo } from 'react'
import { VenueSustainableInitiativesSustainabilityInitiativeFragment } from '../VenueSustainableInitiatives.generated-types'
import { SustainabilityInitiativeCategory } from '../../../types'
import { sustainabilityInitiativeCategoriesToIcons } from '../Icons'
import MuiCloseIcon from '@mui/icons-material/Close'

// TODO: remove this when MUI is used globally throughout the application and set in the theme instead
const CATEGORY_TITLE_TYPOGRAPHY_LINE_HEIGHT: string = '24px'

const SUSTAINABILITY_INITIATIVE_CATEGORIES_TO_CATEGORY_SORT_ORDER: Record<
VenueSustainableInitiativesSustainabilityInitiativeFragment['category'],
number[]
> = {
  CATERING: [31, 30, 27, 28, 25, 26, 29],
  DIVERSITY_AND_INCLUSION: [36, 38, 37],
  ENERGY: [4, 6, 5],
  POLICIES_AND_CREDENTIALS: [16, 17, 19, 20, 21, 22, 23, 24, 18],
  PURCHASING: [33, 32, 34, 35],
  TRAVEL: [3, 1, 2],
  WASTE_AND_RECYCLING: [10, 12, 13, 9, 11, 15],
  WATER: [40, 39],
}

const sortCategorySustainabilityInitiativesByVenueHasSustainabilityInitiativeThenByCategoryOrder =
  (
    sustainabilityInitiativeA: VenueSustainableInitiativesSustainabilityInitiativeFragment,
    sustainabilityInitiativeB: VenueSustainableInitiativesSustainabilityInitiativeFragment,
    selectedSustainabilityInitiatives: VenueSustainableInitiativesSustainabilityInitiativeFragment[],
    category: VenueSustainableInitiativesSustainabilityInitiativeFragment['category']
  ): number => {
    const sustainabilityInitiativeAIsUsedByVenue: boolean =
      selectedSustainabilityInitiatives.find(
        (sustainabilityInitiative) =>
          sustainabilityInitiativeA.id === sustainabilityInitiative.id
      ) !== undefined

    const sustainabilityInitiativeBIsUsedByVenue: boolean =
      selectedSustainabilityInitiatives.find(
        (sustainabilityInitiative) =>
          sustainabilityInitiativeB.id === sustainabilityInitiative.id
      ) !== undefined

    return (
      Number(sustainabilityInitiativeBIsUsedByVenue) -
        Number(sustainabilityInitiativeAIsUsedByVenue) ||
      SUSTAINABILITY_INITIATIVE_CATEGORIES_TO_CATEGORY_SORT_ORDER[
        category
      ].indexOf(sustainabilityInitiativeA.id) -
        SUSTAINABILITY_INITIATIVE_CATEGORIES_TO_CATEGORY_SORT_ORDER[
          category
        ].indexOf(sustainabilityInitiativeB.id)
    )
  }

const renderListItems = (
  category: VenueSustainableInitiativesSustainabilityInitiativeFragment['category'],
  allSustainabilityInitiativesByCategory: Map<
  VenueSustainableInitiativesSustainabilityInitiativeFragment['category'],
  VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  >,
  selectedSustainabilityInitiatives: VenueSustainableInitiativesSustainabilityInitiativeFragment[]
): JSX.Element[] =>
  allSustainabilityInitiativesByCategory
    .get(category)
    .sort((sustainabilityInitiativeA, sustainabilityInitiativeB) =>
      sortCategorySustainabilityInitiativesByVenueHasSustainabilityInitiativeThenByCategoryOrder(
        sustainabilityInitiativeA,
        sustainabilityInitiativeB,
        selectedSustainabilityInitiatives,
        SustainabilityInitiativeCategory.WasteAndRecycling
      )
    )
    .map((value) => (
      <ListItem key={value.id} sx={{ padding: '0px' }}>
        <ListItemText
          primary={
            <Typography
              sx={{
                ...(selectedSustainabilityInitiatives.find(
                  (sustainabilityInitiative) =>
                    sustainabilityInitiative.id === value.id
                ) === undefined
                  ? { textDecoration: 'line-through', color: '#767676' }
                  : { color: '#000000' }),
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              {value.label}
            </Typography>
          }
        />
      </ListItem>
    ))

const renderSustainabilityInitiativeGrid = (
  title: string,
  category: VenueSustainableInitiativesSustainabilityInitiativeFragment['category'],
  allSustainabilityInitiativesByCategory: Map<
  VenueSustainableInitiativesSustainabilityInitiativeFragment['category'],
  VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  >,
  selectedSustainabilityInitiatives: VenueSustainableInitiativesSustainabilityInitiativeFragment[]
): JSX.Element => (
  <Grid container columnGap={1}>
    <Grid item>
      {
        // This box is used to line up the icon with the text so it always is aligned in the middle of the top line of text, even if the text overflows onto multiple lines
      }
      <Box
        display="flex"
        alignItems="center"
        sx={{
          height: CATEGORY_TITLE_TYPOGRAPHY_LINE_HEIGHT,
        }}
      >
        {sustainabilityInitiativeCategoriesToIcons[category]}
      </Box>
    </Grid>
    <Grid item xs>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: CATEGORY_TITLE_TYPOGRAPHY_LINE_HEIGHT,
        }}
      >
        {title}
      </Typography>
      {allSustainabilityInitiativesByCategory.has(category) ? (
        <List sx={{ paddingTop: '16px', paddingBottom: '32px' }}>
          {renderListItems(
            category,
            allSustainabilityInitiativesByCategory,
            selectedSustainabilityInitiatives
          )}
        </List>
      ) : null}
    </Grid>
  </Grid>
)

interface DialogProps {
  title: string
  allSustainabilityInitiatives:
  | VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  selectedSustainabilityInitiatives: VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  open: boolean
  onClose: () => void
}

export const Dialog = ({
  title,
  allSustainabilityInitiatives,
  selectedSustainabilityInitiatives,
  open,
  onClose,
}: DialogProps): JSX.Element => {
  const allSustainabilityInitiativesByCategory: Map<
  VenueSustainableInitiativesSustainabilityInitiativeFragment['category'],
  VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  > = useMemo(
    () =>
      allSustainabilityInitiatives.reduce((previousValue, currentValue) => {
        const currentCategory: VenueSustainableInitiativesSustainabilityInitiativeFragment[] =
          previousValue.get(currentValue.category) ?? []

        previousValue.set(currentValue.category, [
          ...currentCategory,
          currentValue,
        ])

        return previousValue
      }, new Map<VenueSustainableInitiativesSustainabilityInitiativeFragment['category'], VenueSustainableInitiativesSustainabilityInitiativeFragment[]>()),
    [allSustainabilityInitiatives]
  )

  return (
    <MuiDialog open={open} onClose={onClose}>
      <Box sx={{ padding: '24px' }}>
        <Box sx={{ marginBottom: '24px' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '32px',
              color: '#000000',
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            sx={{
              // These positions line up the icon with the dialog title text
              position: 'absolute',
              right: '12px',
              top: '20px',
              color: '#434343',
            }}
          >
            <MuiCloseIcon />
          </IconButton>
        </Box>
        <Grid container>
          <Grid item xs>
            {renderSustainabilityInitiativeGrid(
              'Waste and recycling',
              SustainabilityInitiativeCategory.WasteAndRecycling,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
            {renderSustainabilityInitiativeGrid(
              'Travel',
              SustainabilityInitiativeCategory.Travel,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
            {renderSustainabilityInitiativeGrid(
              'Energy',
              SustainabilityInitiativeCategory.Energy,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
            {renderSustainabilityInitiativeGrid(
              'Water',
              SustainabilityInitiativeCategory.Water,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
            {renderSustainabilityInitiativeGrid(
              'Diversity and inclusion',
              SustainabilityInitiativeCategory.DiversityAndInclusion,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
          </Grid>
          <Grid item xs>
            {renderSustainabilityInitiativeGrid(
              'Catering',
              SustainabilityInitiativeCategory.Catering,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
            {renderSustainabilityInitiativeGrid(
              'Policies/credentials',
              SustainabilityInitiativeCategory.PoliciesAndCredentials,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
            {renderSustainabilityInitiativeGrid(
              'Purchasing',
              SustainabilityInitiativeCategory.Purchasing,
              allSustainabilityInitiativesByCategory,
              selectedSustainabilityInitiatives
            )}
          </Grid>
        </Grid>
      </Box>
    </MuiDialog>
  )
}
